import React, { useState, useEffect, useCallback } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import {
	Box,
	Typography,
	Button,
	IconButton,
	Chip,
	LinearProgress,
} from "@mui/material";
import {
	Close as CloseIcon,
	PlayArrow as PlayArrowIcon,
	Pause as PauseIcon,
	SkipNext as SkipNextIcon,
	SkipPrevious as SkipPreviousIcon,
	FilterList as FilterListIcon,
	Search as SearchIcon,
	Edit as EditIcon,
	TableChart as TableChartIcon,
	Comment as CommentIcon,
	SelectAll as SelectAllIcon,
	Speed as SpeedIcon,
	Security as SecurityIcon,
	Download as DownloadIcon,
	ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";

// Selector for the filters sidebar step (also referenced in the callback so we
// can detect when the user leaves that step).
const SIDEBAR_TARGET =
	'[class*="sidebar"], .sidebar-container, [data-testid="sidebar"]';

// Clicks a tour-controlled element (e.g. a modal/panel close button) if it is
// present in the DOM. Used to auto-close panels the user opened during the tour.
const closeTourModal = (selector) => {
	try {
		const el = document.querySelector(selector);
		if (el) el.click();
	} catch (e) {
		// no-op — the element may not be rendered
	}
};

const SupplyTypeAllocationJoyrideTour = ({ isOpen, onClose }) => {
	const [run, setRun] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(true);

	// Tour steps with improved selectors and content
	const steps = [
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SecurityIcon sx={{ color: "#cc0000" }} />
						Supply Type Allocation Tour
					</Typography>
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						Welcome to the Supply Type Allocation module - the foundation of
						MAN's parts development process. This comprehensive tool helps you
						manage procurement strategies, supplier evaluation, and resource
						planning.
					</Typography>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
						<Chip
							icon={<FilterListIcon />}
							label="Advanced Filtering"
							size="small"
							sx={{ bgcolor: "#e3f2fd" }}
						/>
						<Chip
							icon={<SearchIcon />}
							label="Smart Search"
							size="small"
							sx={{ bgcolor: "#e8f5e8" }}
						/>
						<Chip
							icon={<EditIcon />}
							label="Bulk Operations"
							size="small"
							sx={{ bgcolor: "#fff3e0" }}
						/>
					</Box>
					<Typography
						variant="body2"
						sx={{ color: "#666", fontStyle: "italic" }}
					>
						This tour will guide you through all key features in about 2
						minutes.
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
			styles: {
				options: {
					zIndex: 10000,
				},
			},
		},
		{
			target: SIDEBAR_TARGET,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 2, color: "#1e2832" }}
					>
						Primary Filters Sidebar
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The sidebar provides powerful filtering capabilities to narrow down
						your dataset. You can filter by projects, teams, purchase types, and
						more.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f8f9fa",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e0e0e0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#cc0000" }}
						>
							Key Capabilities:
						</Typography>
						<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
							• Multi-project filtering • SE Team selection • Purchase type
							categorization • Real-time data updates
						</Typography>
					</Box>
				</Box>
			),
			placement: "right",
			spotlightPadding: 10,
		},
		{
			target: '[data-tour-target="supply-search-bar"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SearchIcon sx={{ color: "#cc0000" }} />
						Global Search
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use the global search to quickly find specific parts across all
						fields. Search supports multiple keywords and provides real-time
						suggestions.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e8f5e8",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #4caf50",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#2e7d32" }}
						>
							Search Options:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#2e7d32" }}
						>
							• Part Numbers (PNR, SNR) • Descriptions • Multi-keyword
							combinations • Real-time filtering
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom-start",
			spotlightPadding: 8,
		},
		{
			target: '[data-tour-target="ptsachnr-more-info"]',
			hideNext: true,
			spotlightClicks: true,
			disableBeacon: true,
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SecurityIcon sx={{ color: "#cc0000" }} />
						More Info Button
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Each row has a "More info" button that opens a side panel with
						additional details about the part.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00", display: "block" }}
						>
							👉 Click the "More info" button to continue
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							The tour will move on automatically once the panel opens.
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
		},
		{
			target: '[data-tour-target="ptsachnr-sidepanel"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SecurityIcon sx={{ color: "#cc0000" }} />
						More Info Panel
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						This side panel shows extended details for the selected part number,
						including allocation metadata and additional information.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Expanded Information:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Extended part details • Allocation metadata • Additional
							information
						</Typography>
					</Box>
					<Typography
						variant="caption"
						sx={{ display: "block", mt: 1.5, color: "#666", fontStyle: "italic" }}
					>
						Click Next to continue — we'll close this panel for you.
					</Typography>
				</Box>
			),
			placement: "left",
			spotlightPadding: 8,
		},
		{
			target:
				'.export-button, [class*="export"], button[class*="export"], button:has([class*="download"])',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<DownloadIcon sx={{ color: "#cc0000" }} />
						Export Data
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Export your filtered data to Excel for further analysis, reporting,
						or sharing with stakeholders. The export includes all visible
						columns and applied filters.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e8f5e8",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #4caf50",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#2e7d32" }}
						>
							Export Features:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#2e7d32" }}
						>
							• Excel-compatible format • Respects current filters • Includes
							all visible data • Optimized for analysis
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom",
			spotlightPadding: 8,
		},
		{
			target: '.bulk-edit-button, [class*="bulk-edit"], button[class*="bulk"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<EditIcon sx={{ color: "#cc0000" }} />
						Bulk Edit Operations
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The bulk edit feature allows you to update multiple items
						simultaneously. Save time by applying changes to entire groups of
						parts efficiently.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f3e5f5",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #9c27b0",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#7b1fa2" }}
						>
							Bulk Operations:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#7b1fa2" }}
						>
							• Update SE Team allocations • Modify purchase types • Change
							classifications • Set status flags
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom",
			spotlightPadding: 8,
		},
		{
			target: ".edit-icon",
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 2, color: "#1e2832" }}
					>
						Individual Item Actions
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Each table row provides individual actions for specific part
						management. Access detailed edit options and update item-specific
						information.
					</Typography>
					<Box
						sx={{
							bgcolor: "#e1f5fe",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #03a9f4",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#0277bd" }}
						>
							Individual Actions:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#0277bd" }}
						>
							• Edit specific fields • Track change history • Set item flags
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: ".comment-icon",
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<CommentIcon sx={{ color: "#cc0000" }} />
						Comments & Collaboration
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						The comment system enables team collaboration on specific parts.
						Track discussions, decisions, and important notes for each
						component.
					</Typography>
					<Box
						sx={{
							bgcolor: "#f1f8e9",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #689f38",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#558b2f" }}
						>
							Collaboration Features:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#558b2f" }}
						>
							• Add contextual comments • View comment history • Track user
							contributions
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target: '.flag-toggle-button, .status-toggle, button[title*="Flag"]',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<ToggleOnIcon sx={{ color: "#cc0000" }} />
						Status Toggle Control
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use the status toggle to quickly update the status flags for
						individual parts. This provides immediate visual feedback and
						enables rapid status changes.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fff3e0",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #ff9800",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#f57c00" }}
						>
							Toggle Functions:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#f57c00" }}
						>
							• Quick status updates • Visual status indicators • Immediate save
							functionality • Workflow optimization
						</Typography>
					</Box>
				</Box>
			),
			placement: "left",
			spotlightPadding: 5,
			disableScrolling: true,
		},
		{
			target:
				'thead input[type="checkbox"], .select-all-checkbox, th input[type="checkbox"], table thead th:first-child input',
			content: (
				<Box sx={{ p: 2, maxWidth: 350 }}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SelectAllIcon sx={{ color: "#cc0000" }} />
						Bulk Selection
					</Typography>
					<Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
						Use the selection controls to choose multiple items for bulk
						operations. Perfect for making updates to multiple parts
						simultaneously.
					</Typography>
					<Box
						sx={{
							bgcolor: "#fce4ec",
							borderRadius: 1,
							p: 1.5,
							border: "1px solid #e91e63",
						}}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", color: "#c2185b" }}
						>
							Selection Options:
						</Typography>
						<Typography
							variant="caption"
							sx={{ display: "block", mt: 0.5, color: "#c2185b" }}
						>
							• Select all items on page • Individual item selection • Clear all
							selections
						</Typography>
					</Box>
				</Box>
			),
			placement: "bottom-start",
			spotlightPadding: 8,
		},
		{
			target: "body",
			content: (
				<Box sx={{ p: 3, maxWidth: 450 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: "bold",
							mb: 2,
							color: "#1e2832",
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<SpeedIcon sx={{ color: "#4caf50" }} />
						You're Ready to Begin!
					</Typography>{" "}
					<Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
						You now have the knowledge to effectively use the Supply Type
						Allocation module. Begin by applying filters to find your parts,
						then use the powerful editing features.
					</Typography>
					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", mb: 1.5, color: "#cc0000" }}
					>
						Best Practices:
					</Typography>
					<Box sx={{ mb: 2 }}>
						{[
							"Start with project-level filtering",
							"Use bulk operations for similar parts",
							"Add comments for important decisions",
							"Export data for reporting",
						].map((tip, index) => (
							<Typography
								key={index}
								variant="body2"
								sx={{
									display: "flex",
									alignItems: "center",
									mb: 0.5,
									color: "#555",
								}}
							>
								<Box
									sx={{
										width: 6,
										height: 6,
										bgcolor: "#cc0000",
										borderRadius: "50%",
										mr: 1,
									}}
								/>
								{tip}
							</Typography>
						))}
					</Box>
					<Typography
						variant="body2"
						sx={{
							color: "#666",
							fontStyle: "MAN Europe_Bold",
							textAlign: "center",
							mt: 2,
						}}
					>
						Happy Tracking! 🚚
					</Typography>
				</Box>
			),
			placement: "center",
			disableBeacon: true,
		},
	];

	// Start tour when component opens
	useEffect(() => {
		if (isOpen) {
			setRun(true);
			setStepIndex(0);
			setIsPlaying(true);
		} else {
			setRun(false);
		}
	}, [isOpen]);

	// On the forced "click Additional Filters" step, advance the tour only once
	// the user actually clicks the button (which opens the panel).
	useEffect(() => {
		if (!run) return undefined;
		const currentTarget = steps[stepIndex]?.target;
		const forcedClickTargets = ['[data-tour-target="ptsachnr-more-info"]'];
		if (!forcedClickTargets.includes(currentTarget)) {
			return undefined;
		}
		const btn = document.querySelector(currentTarget);
		if (!btn) return undefined;
		const handler = () => {
			setTimeout(
				() => setStepIndex((i) => Math.min(i + 1, steps.length - 1)),
				350,
			);
		};
		btn.addEventListener("click", handler);
		return () => btn.removeEventListener("click", handler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepIndex, run]);

	// Deterministically collapse panels the user opened once the tour moves to a
	// step that isn't about them. closeTourModal is a no-op when the panel (and
	// therefore its close button) isn't rendered, so this is safe to run on
	// every step change.
	useEffect(() => {
		if (!run) return;
		const currentTarget = steps[stepIndex]?.target;
		// More Info side panel: keep open only on its button/describe steps.
		if (
			currentTarget !== '[data-tour-target="ptsachnr-more-info"]' &&
			currentTarget !== '[data-tour-target="ptsachnr-sidepanel"]'
		) {
			closeTourModal('[data-tour-target="ptsachnr-sidepanel-close"]');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepIndex, run]);

	// Handle tour events
	const handleJoyrideCallback = useCallback(
		(data) => {
			const { action, index, status, type } = data;

			if (type === EVENTS.TARGET_NOT_FOUND) {
				console.warn("Tour target not found, moving to next step");
				// If target is not found, continue to next step
				setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
			} else if ([EVENTS.STEP_AFTER].includes(type)) {
				// Panels the user opened are collapsed deterministically by the
				// stepIndex effect above.
				// Update state to advance tour
				if (action === ACTIONS.PREV) {
					setStepIndex((prev) => Math.max(prev - 1, 0));
				} else {
					// If we're on the last step and user clicks Next/Finish, close the tour
					if (index === steps.length - 1) {
						setRun(false);
						onClose();
					} else {
						setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
					}
				}
			} else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
				// Tour completed or skipped
				closeTourModal('[data-tour-target="ptsachnr-sidepanel-close"]');
				setRun(false);
				onClose();
			} else if (action === ACTIONS.CLOSE) {
				// Handle close button click
				closeTourModal('[data-tour-target="ptsachnr-sidepanel-close"]');
				setRun(false);
				onClose();
			}
		},
		[onClose, steps.length],
	);
	// Control functions
	const handlePlay = () => {
		setIsPlaying(true);
		setRun(true);
	};

	const handlePause = () => {
		setIsPlaying(false);
		setRun(false);
	};

	const handleNext = () =>
		setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
	const handlePrev = () => setStepIndex((prev) => Math.max(prev - 1, 0));

	const handleClose = () => {
		// When user clicks the close (X) button, treat it as skipping the tour
		setRun(false);
		onClose();
	};

	const handleSkipTour = () => {
		// When user clicks "Skip Tour" button, skip the entire tour
		setRun(false);
		onClose();
	};

	// Custom tooltip component
	const TooltipComponent = ({
		continuous,
		index,
		step,
		backProps,
		closeProps,
		primaryProps,
		tooltipProps,
	}) => (
		<Box
			{...tooltipProps}
			sx={{
				backgroundColor: "white",
				borderRadius: 3,
				boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
				maxWidth: 500,
				minWidth: 320,
				...tooltipProps.style,
			}}
		>
			{/* Header */}
			<Box
				sx={{
					background: "linear-gradient(135deg, #1e2832 0%, #2a3a47 100%)",
					color: "white",
					p: 2,
					borderRadius: "10px 10px 0 0",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="body2" sx={{ fontWeight: "bold" }}>
						Step {index + 1} of {steps.length}
					</Typography>
				</Box>{" "}
				<IconButton
					onClick={handleSkipTour}
					size="small"
					sx={{
						color: "white",
						"&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
					}}
					title="Skip Tour"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Progress Bar */}
			<LinearProgress
				variant="determinate"
				value={((index + 1) / steps.length) * 100}
				sx={{
					height: 4,
					"& .MuiLinearProgress-bar": {
						backgroundColor: "#cc0000",
					},
				}}
			/>

			{/* Content */}
			<Box sx={{ p: 0 }}>{step.content}</Box>

			{/* Footer Controls */}
			<Box
				sx={{
					p: 2,
					borderTop: "1px solid #e0e0e0",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					bgcolor: "#f8f9fa",
					borderRadius: "8px",
				}}
			>
				<Button
					{...backProps}
					variant="outlined"
					size="small"
					startIcon={<SkipPreviousIcon />}
					disabled={index === 0}
					sx={{
						borderColor: "#1e2832",
						color: "#1e2832",
						"&:hover": { borderColor: "#cc0000", color: "#cc0000" },
					}}
				>
					Previous
				</Button>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<IconButton
						onClick={isPlaying ? handlePause : handlePlay}
						size="small"
						sx={{ color: "#666" }}
					>
						{isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
					</IconButton>
					<Typography variant="caption" sx={{ color: "#666" }}>
						{index + 1} / {steps.length}
					</Typography>
				</Box>

				{step.hideNext ? (
					<Typography
						variant="caption"
						sx={{
							color: "#cc0000",
							fontWeight: "bold",
							fontSize: "12px",
							alignSelf: "center",
							paddingLeft:"10px"
						}}
					>
						Click highlighted button to continue
					</Typography>
				) : continuous ? (
					<Button
						{...primaryProps}
						variant="contained"
						size="small"
						endIcon={<SkipNextIcon />}
						sx={{
							bgcolor: "#1e2832",
							"&:hover": { bgcolor: "#cc0000" },
						}}
					>
						{index === steps.length - 1 ? "Finish" : "Next"}
					</Button>
				) : (
					<Button
						{...primaryProps}
						variant="contained"
						size="small"
						sx={{
							bgcolor: "#cc0000",
							"&:hover": { bgcolor: "#b30000" },
						}}
					>
						Got it!
					</Button>
				)}
			</Box>
		</Box>
	);
	return (
		<>
			{/* Custom CSS: highlight target by brightening it and dimming the rest */}
			<style>{`
        .react-joyride__spotlight {
          background-color: transparent !important;
          filter: none !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          mix-blend-mode: normal !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.4),
            0 0 16px 3px rgba(255, 255, 255, 0.4) !important;
        }

        .react-joyride__overlay {
          mix-blend-mode: normal !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
        }
      `}</style>
			<Joyride
				steps={steps}
				run={run && isPlaying}
			stepIndex={stepIndex}
			callback={handleJoyrideCallback}
			continuous={true}
			showProgress={false}
			showSkipButton={true}
			hideCloseButton={true}
			disableOverlayClose={true}
			disableScrollParentFix={true}
			disableScrolling={true}
			spotlightClicks={true}
			scrollToFirstStep={false}
			scrollOffset={0}
			tooltipComponent={TooltipComponent}
			styles={{
				options: {
					primaryColor: "#cc0000",
					backgroundColor: "white",
					textColor: "#333",
					zIndex: 10000,
				},
				spotlight: {
					backgroundColor: "transparent",
					border: "none",
					borderRadius: 8,
					boxShadow:
						"0 0 0 9999px rgba(0, 0, 0, 0.4), 0 0 16px 3px rgba(255, 255, 255, 0.4)",
					filter: "none !important",
					opacity: 1,
					mixBlendMode: "normal",
				},
				overlay: {
					mixBlendMode: "normal",
					backgroundColor: "rgba(0, 0, 0, 0.4)",
				},
				beacon: {
					inner: "#cc0000",
					outer: "rgba(204, 0, 0, 0.3)",
				},
				tooltipContainer: {
					textAlign: "left",
				},
				tooltipTitle: {
					color: "#1e2832",
					fontSize: "1.1rem",
					fontWeight: "bold",
				},
				tooltipContent: {
					fontSize: "0.9rem",
					lineHeight: 1.5,
				},
				buttonNext: {
					backgroundColor: "#1e2832",
					color: "white",
					border: "none",
					borderRadius: 6,
					padding: "8px 16px",
					fontSize: "0.85rem",
					fontWeight: "bold",
					cursor: "pointer",
					transition: "all 0.2s ease",
				},
				buttonBack: {
					color: "#1e2832",
					border: "1px solid #1e2832",
					backgroundColor: "transparent",
					borderRadius: 6,
					padding: "8px 16px",
					fontSize: "0.85rem",
					fontWeight: "bold",
					cursor: "pointer",
					transition: "all 0.2s ease",
				},
				buttonSkip: {
					color: "#666",
					fontSize: "0.8rem",
				},
				buttonClose: {
					color: "#666",
					fontSize: "1.2rem",
					right: 10,
					top: 10,
				},
			}}
			locale={{
				back: "Previous",
				close: "Close",
				last: "Finish Tour",
				next: "Next",
				skip: "Skip Tour",
			}}
			/>
		</>
	);
};

export default SupplyTypeAllocationJoyrideTour;
